import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { v7 as Uuid } from 'uuid';

import testApplication from '../backend/default.application.json';
import { normalizeFormData } from './util/normalizeFormData';

const AppContext = createContext();

const initialState = {
  currentStep: 1,
  theme: 'light',
  locale: 'de',
  editMode: false,
  formData: {
    versicherungssumme: null,
    versicherungsbeginn: null,
    zahlungsintervall: 'MONTHLY_CONTINUOUS',
    selectedOptions: [],
  },
  globalConfig: null,
  configuration: {
    products: [],
  },
  selectedProduct: {},
  applicationObject: testApplication,
  qualifiedApplication: {},
  sessionId: Uuid(),
  applicationId: null,
  availablePaymentPeriods: [
    { label: 'monatlich', code: 'MONTHLY_CONTINUOUS', factor: 1 },
    { label: 'vierteljährlich', code: 'QUARTERLY_CONTINUOUS', factor: 3 },
    { label: 'halbjährlich', code: 'SIX_MONTH_CONTINUOUS', factor: 6 },
    { label: 'jährlich', code: 'ANNUALLY_CONTINUOUS', factor: 12 },
  ],
  sessionExpirationDate: Date.now() + 60 * 60 * 1000,
};

function appReducer(state, action) {
  if (state.sessionExpirationDate < Date.now()) {
    // Wenn die Sitzung abgelaufen ist, wird man wieder auf die erste Seite zurückgeworfen.
    // Eine Meldung an den User wäre hier gut.
    window.location.href = '/tarif-berechnen';
  }
  state.sessionExpirationDate = Date.now() + 60 * 60 * 1000;
  switch (action.type) {
    case 'SET_GLOBAL_CONFIG':
      return {
        ...state,
        globalConfig: action.payload,
      };

    case 'SET_FORM_DATA':
      return {
        ...state,
        formData: {
          ...state.formData,
          ...normalizeFormData(action.payload),
        },
      };
    case 'SET_SELECTED_PRODUCT': {
      return {
        ...state,
        selectedProduct: action.payload,
      };
    }
    case 'SET_CURRENT_STEP':
      return {
        ...state,
        currentStep: action.payload,
      };
    case 'SET_THEME':
      return {
        ...state,
        theme: action.payload,
      };
    // In AppContext.js - Reducer erweitern
    case 'SET_SESSION_ID':
      return {
        ...state,
        sessionId: action.payload,
      };

    case 'SET_APPLICATION_DATA':
      return {
        ...state,
        applicationId: action.payload.applicationId,
        applicationObject: action.payload.applicationObject,
        email: action.payload.email || state.formData.email,
      };

    case 'SET_APPLICATION_ID':
      return {
        ...state,
        applicationId: action.payload,
      };

    case 'SET_QUALIFIED_APPLICATION':
      return {
        ...state,
        qualifiedApplication: action.payload.qualifiedApplication,
      };
    case 'SET_DOCUMENT_LINKS':
      return {
        ...state,
        documentLinks: {
          ...state.documentLinks,
          ...action.payload,
        },
      };
    case 'RESET_APPLICATION':
      return {
        ...initialState,
        // Behält nur grundlegende Einstellungen bei
        theme: state.theme,
        language: state.language,
        globalConfig: state.globalConfig,
        configuration: state.configuration,
      };

    case 'SET_CONFIGURATION':
      return {
        ...state,
        configuration: action.payload,
      };

    case 'SET_EDIT_MODE':
      return {
        ...state,
        editMode: action.payload,
      };

    case 'RESET_FORM':
      return {
        ...state,
        formData: initialState.formData,
      };

    default:
      return state;
  }
}

export function AppProvider({ children, globalConfig, configuration }) {
  const [state, dispatch] = useReducer(appReducer, { ...initialState, globalConfig, configuration });

  useEffect(() => {
    if (globalConfig) {
      dispatch({ type: 'SET_GLOBAL_CONFIG', payload: globalConfig });
    }
  }, [globalConfig]);

  useEffect(() => {
    if (configuration) {
      dispatch({ type: 'SET_CONFIGURATION', payload: configuration });
    }
  }, [configuration]);

  return <AppContext.Provider value={{ state, dispatch }}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext muss innerhalb eines AppProviders verwendet werden');
  }
  return context;
}
